import { useLayoutEffect, useRef, useState } from 'react';
import { useInterval } from '@elfsight/lib-utils';
import { getConfig } from './get-config';
import { getScheduleSettings } from './get-schedule-settings';
import { displaySolvers } from './repeat-solvers';
import type { ScheduleDefaultsType } from './schedule-defaults';
import { getCycleIndex } from './utils/get-cycle-index';

interface UseDisplayInScheduleProps<T extends ScheduleDefaultsType> {
  settings: T;
  enabled?: boolean;
  onRestart?: () => void;
  _debugUserDate?: Date;
}

export function useDisplayInSchedule<T extends ScheduleDefaultsType>({
  settings,
  enabled,
  onRestart,
  _debugUserDate
}: UseDisplayInScheduleProps<T>) {
  const scheduleSettings = getScheduleSettings(settings);
  const config = getConfig(scheduleSettings, _debugUserDate);
  const previousCycleIndexRef = useRef(getCycleIndex(config));

  const solver = displaySolvers[config.repeatType];

  const [display, setDisplay] = useState(solver?.(config) ?? false);

  const startTime = config.startDate?.getTime();
  const endTime = config.endDate?.getTime();
  const debugUserDate = _debugUserDate?.getTime();

  useLayoutEffect(() => {
    if (!solver) {
      return;
    }

    const currentDisplay = solver(config);
    setDisplay(currentDisplay);
    // oxlint-disable-next-line exhaustive-deps
  }, [solver, startTime, endTime, debugUserDate]);

  useInterval(() => {
    if (!solver) {
      return;
    }

    const newConfig = getConfig(scheduleSettings, _debugUserDate);
    const newDisplay = solver(newConfig);
    setDisplay(newDisplay);
    const newCycleIndex = getCycleIndex(newConfig);

    if (newCycleIndex !== previousCycleIndexRef.current) {
      previousCycleIndexRef.current = newCycleIndex;
      onRestart?.();
    }
  }, 60_000); // 1 minute

  if (!enabled) {
    return true;
  }

  return display;
}
